import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import { useProductChangeLogsQuery, type ProductChangeItem } from '../hooks/useProductChangeLogsQuery';

const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return iso || '-';
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const show = (v: string | null) => (v == null || v === '' ? '(없음)' : v);

const ChangeLines = ({ changes, side }: { changes: ProductChangeItem[]; side: 'before' | 'after' }) => (
  <ul className="flex flex-col gap-0.5">
    {changes.map((c, i) => (
      <li key={i} className="[overflow-wrap:anywhere]">
        <span className="text-[color:var(--content-muted)]">{c.label} : </span>
        {show(c[side])}
      </li>
    ))}
  </ul>
);

/**
 * QA-515 추가 — 상품 [변경 이력]. 고도몰 상품 수정의 변경 이력과 같은 표(수정일시·관리자·분류·수정 전·수정 후), 최근 것이 위.
 * 연 뒤에만 불러온다. BE 반영 전이면 안내만 보인다.
 */
export const ProductChangeLogDialog = ({
  productId,
  open,
  onOpenChange,
}: {
  productId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const query = useProductChangeLogsQuery(productId, open);
  // 같은 저장의 여러 분류(같은 changedAt)는 받은 순서대로 붙여 둔다(안정 정렬).
  const rows = [...(query.data ?? [])].sort((a, b) => b.changedAt.localeCompare(a.changedAt));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl" data-testid="product-change-log-dialog">
        <DialogHeader>
          <DialogTitle>변경 이력</DialogTitle>
          <DialogDescription>
            이 상품을 언제 누가 무엇을 바꿨는지 보여 줍니다(이력 기록을 시작한 뒤의 변경, 최근 200건).
          </DialogDescription>
        </DialogHeader>

        {query.isPending ? (
          <Skeleton className="h-[160px] w-full" />
        ) : query.isError ? (
          <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>
            {friendlyMessage(query.error, '변경 이력을 불러오지 못했습니다.')}
          </p>
        ) : query.data === null ? (
          <p className="text-sm text-[color:var(--content-muted)]" data-testid="product-change-log-not-ready">
            변경 이력은 서버 반영 후 볼 수 있습니다.
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-[color:var(--content-muted)]" data-testid="product-change-log-empty">
            남은 변경 이력이 없습니다.
          </p>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto">
            <Table data-testid="product-change-log-table">
              <TableHeader style={{ background: 'var(--surface-1)' }}>
                <TableRow>
                  <TableHead scope="col" className="w-[150px]">수정일시</TableHead>
                  <TableHead scope="col" className="w-[130px]">관리자</TableHead>
                  <TableHead scope="col" className="w-[100px]">분류</TableHead>
                  <TableHead scope="col">수정 전</TableHead>
                  <TableHead scope="col">수정 후</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={`${r.changedAt}-${i}`} data-testid="product-change-log-row">
                    <TableCell className="align-top text-[13px]">{formatDateTime(r.changedAt)}</TableCell>
                    <TableCell className="align-top text-[13px]">
                      <div className="flex flex-col leading-tight">
                        <span>{r.adminName ?? r.adminLoginId ?? r.adminUserId ?? '-'}</span>
                        {r.adminName && r.adminLoginId ? (
                          <span className="text-[11px] text-[color:var(--content-muted)]">{r.adminLoginId}</span>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="align-top text-[13px]">
                      <div className="flex flex-col leading-tight">
                        <span>{r.category ?? '-'}</span>
                        {r.action ? <span className="text-[11px] text-[color:var(--content-muted)]">{r.action}</span> : null}
                      </div>
                    </TableCell>
                    <TableCell className="align-top text-[13px]">
                      <ChangeLines changes={r.changes} side="before" />
                    </TableCell>
                    <TableCell className="align-top text-[13px]">
                      <ChangeLines changes={r.changes} side="after" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/** 상품 수정 상단 [변경 이력] 버튼 — 누르면 팝업을 열고 그때 불러온다. */
export const ProductChangeLogButton = ({ productId }: { productId: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="product-change-log-open">
        변경 이력
      </Button>
      {open ? <ProductChangeLogDialog productId={productId} open onOpenChange={setOpen} /> : null}
    </>
  );
};
